import { motion, AnimatePresence } from "framer-motion";

/**
 * OracleCards - Four mystical oracle cards for mood/feeling selection
 * Each card represents an emotional state with unique visual design
 * Styled as ancient, ornate cards laid out on a table
 */
export const OracleCards = ({ options, selected, onSelect }) => {
    // Card designs for each mood
    const cardDesigns = {
        quiet: {
            symbol: "🌙",
            gradient: "linear-gradient(145deg, #1a1a2e 0%, #16213e 50%, #0f0f23 100%)",
            accent: "#4a5568",
            pattern: "M50,20 Q70,50 50,80 Q30,50 50,20", // Moon crescent
            ornament: "circle"
        },
        heavy: {
            symbol: "🌫️",
            gradient: "linear-gradient(145deg, #2d2d2d 0%, #1a1a1a 50%, #0d0d0d 100%)",
            accent: "#718096",
            pattern: "M30,30 Q50,20 70,30 Q80,50 70,70 Q50,80 30,70 Q20,50 30,30", // Cloud/weight
            ornament: "square"
        },
        restless: {
            symbol: "🔥",
            gradient: "linear-gradient(145deg, #4a1c1c 0%, #2d1515 50%, #1a0a0a 100%)",
            accent: "#e53e3e",
            pattern: "M50,80 L30,50 L40,50 L30,20 L50,40 L60,20 L50,50 L70,50 Z", // Flame
            ornament: "diamond"
        },
        ready: {
            symbol: "✨",
            gradient: "linear-gradient(145deg, #3d3d1a 0%, #2a2a12 50%, #1a1a0a 100%)",
            accent: "#D4AF37",
            pattern: "M50,15 L55,40 L80,40 L60,55 L70,80 L50,65 L30,80 L40,55 L20,40 L45,40 Z", // Star
            ornament: "star"
        }
    };

    return (
        <div className="grid grid-cols-2 gap-4 sm:gap-6 w-full max-w-md mx-auto">
            {options.map((option, index) => {
                const isSelected = selected === option.value;
                const design = cardDesigns[option.value] || cardDesigns.quiet;
                
                return (
                    <motion.button
                        key={option.value}
                        onClick={() => onSelect(option.value)}
                        className="relative focus:outline-none group"
                        initial={{ opacity: 0, y: 30, rotate: (index % 2 === 0 ? -2 : 2) }}
                        animate={{ 
                            opacity: 1, 
                            y: 0,
                            rotate: isSelected ? 0 : (index % 2 === 0 ? -2 : 2),
                            scale: isSelected ? 1.05 : 1
                        }}
                        transition={{ 
                            duration: 0.5, 
                            delay: index * 0.1,
                            scale: { duration: 0.2 }
                        }}
                        whileHover={{ scale: 1.03, rotate: 0 }}
                        whileTap={{ scale: 0.98 }}
                        data-testid={`oracle-card-${option.value}`}
                    >
                        {/* Selection glow */}
                        <AnimatePresence>
                            {isSelected && (
                                <motion.div
                                    className="absolute -inset-2 rounded-lg"
                                    style={{
                                        background: `radial-gradient(ellipse, ${design.accent}40 0%, transparent 70%)`,
                                        filter: "blur(8px)"
                                    }}
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                />
                            )}
                        </AnimatePresence>

                        {/* Card body */}
                        <div
                            className="relative w-full aspect-[3/4] rounded-lg overflow-hidden"
                            style={{
                                background: design.gradient,
                                border: isSelected 
                                    ? `2px solid ${design.accent}` 
                                    : "1px solid rgba(212,175,55,0.2)",
                                boxShadow: isSelected
                                    ? `0 0 25px ${design.accent}50, 0 10px 40px rgba(0,0,0,0.7)`
                                    : "0 8px 25px rgba(0,0,0,0.5)"
                            }}
                        >
                            {/* Ornate border pattern */}
                            <div className="absolute inset-2 rounded border border-primary/20" />
                            <div className="absolute inset-3 rounded border border-primary/10" />
                            
                            {/* Corner ornaments */}
                            <svg className="absolute top-2 left-2 w-6 h-6 text-primary/30">
                                <path d="M0,15 L0,5 Q0,0 5,0 L15,0" fill="none" stroke="currentColor" strokeWidth="1" />
                            </svg>
                            <svg className="absolute top-2 right-2 w-6 h-6 text-primary/30">
                                <path d="M15,0 L5,0 Q0,0 0,5 L0,15" fill="none" stroke="currentColor" strokeWidth="1" transform="translate(6,0)" />
                            </svg>
                            <svg className="absolute bottom-2 left-2 w-6 h-6 text-primary/30">
                                <path d="M0,0 L0,10 Q0,15 5,15 L15,15" fill="none" stroke="currentColor" strokeWidth="1" />
                            </svg>
                            <svg className="absolute bottom-2 right-2 w-6 h-6 text-primary/30">
                                <path d="M15,15 L5,15 Q0,15 0,10 L0,0" fill="none" stroke="currentColor" strokeWidth="1" transform="translate(6,0)" />
                            </svg>

                            {/* Center mystical pattern */}
                            <div className="absolute inset-0 flex items-center justify-center">
                                <motion.svg
                                    viewBox="0 0 100 100"
                                    className="w-24 h-24 sm:w-28 sm:h-28"
                                    animate={isSelected ? { rotate: 360 } : {}}
                                    transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
                                >
                                    {/* Outer circle */}
                                    <circle
                                        cx="50" cy="50" r="45"
                                        fill="none"
                                        stroke={design.accent}
                                        strokeWidth="0.5"
                                        opacity={isSelected ? 0.6 : 0.2}
                                    />
                                    {/* Inner design pattern */}
                                    <motion.path
                                        d={design.pattern}
                                        fill="none"
                                        stroke={design.accent}
                                        strokeWidth="1.5"
                                        opacity={isSelected ? 0.9 : 0.4}
                                        animate={isSelected ? {
                                            strokeOpacity: [0.7, 1, 0.7]
                                        } : {}}
                                        transition={{ duration: 2, repeat: Infinity }}
                                    />
                                    {/* Decorative dots */}
                                    {[0, 90, 180, 270].map((deg) => (
                                        <circle
                                            key={deg}
                                            cx={50 + 40 * Math.cos((deg * Math.PI) / 180)}
                                            cy={50 + 40 * Math.sin((deg * Math.PI) / 180)}
                                            r="2"
                                            fill={design.accent}
                                            opacity={isSelected ? 0.8 : 0.3}
                                        />
                                    ))}
                                </motion.svg>
                            </div>

                            {/* Central symbol */}
                            <div className="absolute inset-0 flex items-center justify-center">
                                <motion.span
                                    className="text-3xl sm:text-4xl"
                                    style={{
                                        filter: isSelected 
                                            ? `drop-shadow(0 0 15px ${design.accent})`
                                            : "none"
                                    }}
                                    animate={isSelected ? {
                                        scale: [1, 1.15, 1],
                                    } : {}}
                                    transition={{ duration: 2, repeat: Infinity }}
                                >
                                    {design.symbol}
                                </motion.span>
                            </div>

                            {/* Card label at bottom */}
                            <div className="absolute bottom-4 left-0 right-0 text-center">
                                <motion.span
                                    className="font-cinzel text-xs sm:text-sm uppercase tracking-widest"
                                    style={{
                                        color: isSelected ? design.accent : "rgba(212,175,55,0.5)"
                                    }}
                                    animate={isSelected ? {
                                        textShadow: [
                                            `0 0 5px ${design.accent}50`,
                                            `0 0 15px ${design.accent}80`,
                                            `0 0 5px ${design.accent}50`
                                        ]
                                    } : {}}
                                    transition={{ duration: 2, repeat: Infinity }}
                                >
                                    {option.label}
                                </motion.span>
                            </div>

                            {/* Worn/aged texture overlay */}
                            <div 
                                className="absolute inset-0 pointer-events-none opacity-20"
                                style={{
                                    backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
                                    mixBlendMode: "overlay"
                                }}
                            />
                        </div>
                    </motion.button>
                );
            })}
        </div>
    );
};

export default OracleCards;
